/* eslint-disable jsx-a11y/no-static-element-interactions */
import Slider from '@/components/Slider';
import useIsMobile from '@/hooks/useIsMobile';
import classNames from 'classnames';
import './style.css';
import { useQuery } from '@apollo/client';
import Button from '@/components/Button';
import dayjs from 'dayjs';
import {
  Enum_Favorite_Type,
  FetchWildcardsQuery,
  FetchWildcardsQueryVariables,
  WildcardEntity,
} from '@/graphql/generated/graphql';
import LikeButton from '@/components/LikeButton';
import { buildRouteUrl, getFirstValidRoute } from '@/utils/navigation';
import SubContainer from '@/components/Container/SubContainer';
import { QUERY_FETCH_WILDCARDS } from '@/graphql/queries/wildcard/wildcard';

export const gradients = {
  insight: 'insight-class',
  mindful: 'mindful-class',
  emotion: 'emotion-class',
  quote: 'quote-class',
  wildcard: 'wildcard-class',
};
interface WildcardsProps {
  renderTitle: () => JSX.Element;
}

export default function Wildcards({ renderTitle }: WildcardsProps) {
  const { isMobile } = useIsMobile();

  const { data: wildcardsData, loading } = useQuery<FetchWildcardsQuery, FetchWildcardsQueryVariables>(
    QUERY_FETCH_WILDCARDS,
    {
      variables: { today: dayjs().format('YYYY-MM-DD') },
    },
  );

  const hasContent = wildcardsData?.wildcards?.data && wildcardsData?.wildcards?.data.length > 0;

  const renderTaskCard = (wildCard: WildcardEntity) => {
    const renderActivity = () => {
      const wildcardAttributes = wildCard?.attributes;

      let wildcardHref = '#';
      const route = getFirstValidRoute(wildCard?.attributes?.cta);
      if (route) {
        const href = buildRouteUrl(route);
        wildcardHref = href;
      }

      return (
        <div className="flex flex-col items-center md:flex-row">
          <div className="max-h-[125px] min-h-[125px] min-w-[125px] max-w-[125px] overflow-hidden rounded-full md:mr-[30px]">
            {wildCard.attributes?.internal && (
              <div
                className={`h-[125px] w-[125px] bg-cover bg-center`}
                style={{
                  backgroundImage: `url('${wildcardAttributes?.image?.data?.attributes?.url}')`,
                  transformOrigin: 'center',
                }}
              />
            )}
            {!wildCard.attributes?.internal && (
              <div className="flex h-[125px] w-[125px] items-center justify-center rounded-full bg-white">
                <img src={wildcardAttributes?.image?.data?.attributes?.url} alt="logo" className="h-[36px] w-[90px] " />
              </div>
            )}
          </div>

          <div className="flex flex-col items-center md:items-start">
            <h3 className="!m-0 text-center font-larken text-sh-18-28 md:text-start">{wildcardAttributes?.title}</h3>
            <p className="mt-2 text-center text-sh-12-18 md:text-start">{wildcardAttributes?.description}</p>
            {wildCard?.attributes?.cta && (
              <Button as="a" className="mt-2 text-center" href={wildcardHref}>
                {wildcardAttributes?.ctaText}
              </Button>
            )}
            {!wildcardAttributes?.cta && (
              <LikeButton
                isButtonLayout
                label={wildcardAttributes?.ctaText}
                entityId={wildCard!.id || ''}
                type={Enum_Favorite_Type.Wildcard}
              />
            )}
          </div>
        </div>
      );
    };

    return (
      <div
        className={classNames(
          'group h-full w-[300px] overflow-hidden rounded-2xl border-2 border-transparent md:w-full',
        )}>
        <div
          className={classNames(
            'item flex h-full flex-col justify-center px-sh-30 py-sh-15 ' +
              `daily-bubble-radial ${gradients['wildcard']}`,
          )}>
          {renderActivity()}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (loading) {
      return [<div key="loading" className="h-[168px] w-[610px] animate-pulse rounded-md bg-gray-300"></div>];
    }

    if (!wildcardsData?.wildcards?.data?.length) {
      return [];
    }

    return wildcardsData.wildcards.data.map((wildcard) => renderTaskCard(wildcard as WildcardEntity));
  };

  if (!loading && !hasContent) {
    return <></>;
  }

  if (!isMobile) {
    return (
      <>
        <div className="mt-[30px] w-full">{renderTitle()}</div>
        <SubContainer className="md:flex md:gap-4">
          <div className="grid grid-cols-1 gap-[16px] sm:grid-cols-2">{renderContent()}</div>;
        </SubContainer>
      </>
    );
  }

  return (
    <>
      <div className="mt-[30px] w-full">{renderTitle()}</div>
      <SubContainer className="md:flex md:gap-4">
        {hasContent && (
          <Slider
            disableResponsiveDots={true}
            slideClassName="!max-h-none"
            sliders={renderContent()}
            options={{
              disabled: true,
              breakpoints: {
                '(max-width: 768px)': {
                  disabled: false,
                  loop: true,
                  renderMode: 'performance',
                  slides: { perView: 1.2, spacing: 16 },
                },
              },
            }}
          />
        )}
        {!hasContent && renderContent()}
      </SubContainer>
    </>
  );
}
